import http from '../../shared/http.js';
import { withFlash } from '../../shared/flash.js';
import { confirmAction, hideLoading, notifyError, showLoading } from '../../shared/notify.js';

// The contacts of a customer, managed from the customer's own page. There is no
// standalone list, so everything here works against one section of the detail
// page and reloads it when something changes.

// Reloads the page showing a flash. The table is rendered server-side, so a
// round trip is what puts a new row on screen - and it also refreshes anything
// else the change touched. The list is short enough that the reload is cheaper
// than keeping a client-side copy of it in step.
function reloadWith(key) {
  window.location.href = withFlash(window.location.pathname, key);
}

// 'team' is the one Anrede that is not a person. Kept as a literal here rather
// than imported, because utils/anredeArten.js is CommonJS and this file is the
// ESM half of the app - the value is part of the API contract either way.
const ANREDE_TEAM = 'team';

// Shows the first name for a person and hides it for a team, and keeps the
// second field's label honest: for a team that one field carries the whole name.
//
// Hidden rather than disabled, because a hidden input is still submitted - which
// is why readForm drops the field too. Doing only one of the two would either
// send an empty vorname that clears a person's name on a PATCH, or leave a
// stale one on a contact that is no longer a person.
function toggleVorname(form) {
  const anrede = form.querySelector('#ap-anrede')?.value;
  const feld = document.getElementById('ap-vorname-feld');
  const label = document.getElementById('ap-nachname-label');
  const team = anrede === ANREDE_TEAM;

  if (feld) feld.classList.toggle('hidden', team);
  if (label) label.textContent = team ? 'Name' : 'Nachname';
}

// Fills the dialog for a new contact or for an existing one. One dialog for
// both, because a second would be the same fields twice - the hidden id
// is what tells the submit handler which of the two it is.
function fillForm(form, dataset = null) {
  const titel = document.getElementById('ansprechperson-modal-titel');

  form.querySelector('#ansprechperson-id').value = dataset?.id || '';
  form.querySelector('#ap-anrede').value = dataset?.anrede || 'unbekannt';
  form.querySelector('#ap-vorname').value = dataset?.vorname || '';
  form.querySelector('#ap-nachname').value = dataset?.nachname || '';
  form.querySelector('#ap-funktion').value = dataset?.funktion || '';
  form.querySelector('#ap-email').value = dataset?.email || '';
  form.querySelector('#ap-telefon').value = dataset?.telefon || '';
  form.querySelector('#ap-mobile').value = dataset?.mobile || '';
  form.querySelector('#ap-notiz').value = dataset?.notiz || '';
  // Same string comparison as `aktiv` below, for the same reason. A new contact
  // starts without the flag: nobody has asked not to be written to yet.
  form.querySelector('#ap-keine-emails').checked = dataset?.keineEmails === 'true';
  // Ranks above 3 collapse onto the "Weitere" option, which is the same choice
  // the table makes when it prints the bare number.
  form.querySelector('#ap-rang').value = Math.min(Number(dataset?.rang) || 1, 3);
  // Compared as a string, because a dataset value always is one and 'false'
  // would otherwise be truthy. The template stringifies the field for exactly
  // this comparison - writing `data-aktiv=item.aktiv` there makes Pug render
  // the attribute the HTML boolean way, bare or absent, and this reads false
  // either way. New contacts start active.
  form.querySelector('#ap-aktiv').checked = dataset ? dataset.aktiv === 'true' : true;

  // After the value is in, so the fields match the contact being edited rather
  // than whoever was in the dialog last.
  toggleVorname(form);

  if (titel) {
    titel.textContent = dataset ? 'Ansprechperson bearbeiten' : 'Ansprechperson hinzufügen';
  }
}

// Turns the dialog into the JSON body the API expects.
function readForm(form) {
  const data = Object.fromEntries(new FormData(form).entries());

  // Not a field on the contact - it says which request to send.
  delete data.id;

  // An unchecked checkbox is not submitted at all, so the absent key is the
  // "false" the server needs to see.
  data.aktiv = form.querySelector('#ap-aktiv').checked;
  data.keineEmails = form.querySelector('#ap-keine-emails').checked;
  data.rang = Number(data.rang);

  // Optional text fields are sent as '' when cleared rather than dropped:
  // dropping one would mean "leave unchanged" on a PATCH, so clearing an email
  // would never reach the record.
  ['funktion', 'email', 'telefon', 'mobile', 'notiz'].forEach((field) => {
    data[field] = (data[field] || '').trim();
  });

  // A team has no first name. The field is hidden but still submitted, so it is
  // dropped here rather than sent as '' - the server clears it anyway, and an
  // empty string would read as "clear this" on a person's PATCH.
  if (data.anrede === ANREDE_TEAM) delete data.vorname;

  return data;
}

export function initAnsprechpersonInline() {
  const liste = document.getElementById('ansprechperson-liste');
  if (!liste) return;

  const modal = document.getElementById('ansprechperson-modal');

  // Switching the Anrede reshapes the form straight away, so somebody picking
  // Team does not leave a first name filled in behind a hidden field.
  const anredeFeld = document.getElementById('ap-anrede');
  if (anredeFeld) {
    anredeFeld.addEventListener('change', () => {
      toggleVorname(document.getElementById('ansprechperson-form'));
    });
  }
  const form = document.getElementById('ansprechperson-form');
  const { kundeId } = liste.dataset;

  document.getElementById('ansprechperson-neu')?.addEventListener('click', () => {
    fillForm(form);
    modal.showModal();
  });

  form.querySelector('[data-action="abbrechen"]')?.addEventListener('click', () => modal.close());

  // One delegated listener for the whole table, so rows cost nothing to add.
  liste.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-action]');
    if (!button) return;

    if (button.dataset.action === 'edit') {
      fillForm(form, button.dataset);
      modal.showModal();
      return;
    }

    if (button.dataset.action !== 'delete') return;

    const { id, name } = button.dataset;
    const confirmed = await confirmAction({
      title: 'Ansprechperson löschen',
      message: `Möchten Sie ${name} endgültig löschen? Das kann nicht rückgängig gemacht werden.`,
      okText: 'Löschen'
    });
    if (!confirmed) return;

    button.disabled = true;
    showLoading('Ansprechperson wird gelöscht …');

    try {
      await http.delete(`/ansprechpersonen/${id}`);
      reloadWith('ansprechperson-geloescht');
    } catch (err) {
      hideLoading();
      button.disabled = false;
      notifyError(
        err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
      );
    }
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const id = form.querySelector('#ansprechperson-id').value;
    const data = readForm(form);
    const submitButton = form.querySelector('button[type="submit"]');

    if (submitButton) submitButton.disabled = true;
    showLoading('Wird gespeichert …');

    try {
      // Editing goes to the contact's own route; creating goes to the
      // customer's nested one, since that is where the parent comes from.
      if (id) await http.patch(`/ansprechpersonen/${id}`, data);
      else await http.post(`/kunden/${kundeId}/ansprechpersonen`, data);

      reloadWith(id ? 'ansprechperson-aktualisiert' : 'ansprechperson-erstellt');
    } catch (err) {
      hideLoading();
      if (submitButton) submitButton.disabled = false;
      notifyError(
        err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
      );
    }
  });
}
